import { createInitialTableV2WidgetRuntimeState, tableV2WidgetRuntimeReducer } from './state';
import type { TableV2TableRun, TableV2TableRunInput, TableV2WidgetRuntimeState } from './state';
import { TableV2FilterModel } from './TableV2FilterModel';
import { TableV2GridModel, tableV2GridStateSchema } from './TableV2GridModel';
import type { TableV2GridState } from './TableV2GridModel';
import { TableV2LinkingModel } from './TableV2LinkingModel';
import { TableV2TableQueryModel } from './TableV2TableQuery';
import { TableV2TableSelectionModel } from './TableV2TableSelectionModel';
import type { TableV2TableSelectionPersistedState } from './TableV2TableSelectionModel';
import { computed, createModel, effect, signal, untracked } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { GridRowSelectionChange } from '@thinkalpha/ui-table-svelte/gridViewProps';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { TableClass } from '@thinkalpha/ui-types/contracts/workspace';
import type { TableV2WidgetModel as TableV2WidgetContractModel } from '@thinkalpha/ui-types/contracts/workspace';
import { StickyFilterModel, stickyFilterStateSchema } from 'src/models/StickyFilterModel';
import type { StickyFilterModelEventBus, StickyFilterState } from 'src/models/StickyFilterModel';
import type { TableModel } from 'src/models/TableModel';
import type { ProxyClientService } from 'src/services/ProxyClientService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { WidgetContainerChannelSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import type { WidgetContainerChannelSliceEventBus } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import { WidgetContainerTableSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTableSlice';
import type { WidgetContainerTableSliceEventBus } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTableSlice';
import { WidgetContainerTabSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerTabSliceEventBus } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import type {
    StickyFilterSliceEvents,
    StickyFilterSliceSignals,
    TableWidgetEvents,
    TableWidgetSignals,
} from 'src/types/EventBus';
import { MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

const tableKeySchema = z.object({ sym: z.string(), ex: z.string() });

export const tableV2WidgetStateSchema = tableV2GridStateSchema.extend({
    table: tableKeySchema.nullable().default(null),
    class: z.enum(TableClass).nullable().default(null),
    filter: stickyFilterStateSchema.shape.filter,
});

export type TableV2WidgetState = TableV2GridState & TableV2TableSelectionPersistedState & StickyFilterState;

type TableV2WidgetContent = Omit<TableV2WidgetContractModel, 'type' | 'channelId'>;
const _assertStateIsWireCompatible = (state: TableV2WidgetState): TableV2WidgetContent => state;
void _assertStateIsWireCompatible;

export type TableV2WidgetEventBus = EventBus<
    EventBusSignals<WidgetContainerChannelSliceEventBus> &
        TableWidgetSignals &
        StickyFilterSliceSignals &
        EventBusSignals<WidgetContainerTableSliceEventBus> &
        EventBusSignals<WidgetContainerTabSliceEventBus>,
    EventBusEvents<WidgetContainerChannelSliceEventBus> &
        TableWidgetEvents &
        StickyFilterSliceEvents &
        EventBusEvents<WidgetContainerTableSliceEventBus> &
        EventBusEvents<WidgetContainerTabSliceEventBus>
>;

export const TableV2SelfOwnedModel = createModel(
    (init: {
        container: WidgetContainerController;
        tabId: WidgetId;
        persisted: TableV2WidgetState;
        logger: Logger;
        proxyClientService: ProxyClientService;
        table: TableModel;
        workspaceService: Pick<WorkspaceService, 'publishLinkIntent' | 'subscribeLinkEvents'>;
    }) => {
        const linking: { current: TableV2LinkingModel | undefined } = { current: undefined };
        const channelSlice = new WidgetContainerChannelSlice({
            container: init.container,
            tabId: init.tabId,
            onResetChannelData: () => linking.current?.resetChannelData(),
        });
        const tableSlice = new WidgetContainerTableSlice({ table: init.table });
        const widgetTabSlice = new WidgetContainerTabSlice({
            container: init.container,
            tabId: init.tabId,
            logger: init.logger,
        });
        const tableSelection = new TableV2TableSelectionModel({
            persisted: init.persisted,
            proxyClientService: init.proxyClientService,
        });
        const grid = new TableV2GridModel({
            persisted: init.persisted,
            columns: init.table.columnsSignal,
            tableClass: tableSelection.tableClass,
        });
        const linkingModel = new TableV2LinkingModel({
            channel: channelSlice.channel,
            logger: init.logger,
            selectedTableKey: tableSelection.selectedTableKey,
            tabId: init.tabId,
            tableClass: tableSelection.tableClass,
            workspaceService: init.workspaceService,
        });
        linking.current = linkingModel;
        effect(() => () => linkingModel[Symbol.dispose]());
        const stickyFilter = new StickyFilterModel({ persisted: init.persisted });
        const filterModel = new TableV2FilterModel({
            columns: init.table.columnsSignal,
            linkingFilter: linkingModel.linkFilter,
            stickyFilter: stickyFilter.eventBus.getSignal('widget::stickyFilterValue'),
        });

        const runtimeState = signal<TableV2WidgetRuntimeState>(createInitialTableV2WidgetRuntimeState());
        const dispatchRuntime = (action: Parameters<typeof tableV2WidgetRuntimeReducer>[1]): void => {
            const next = tableV2WidgetRuntimeReducer(runtimeState.value, action);
            if (next !== runtimeState.value) runtimeState.value = next;
        };

        const requestedRun = computed<TableV2TableRunInput | null>(() => {
            const tableKey = tableSelection.table.value;
            if (tableKey === null) return null;
            return {
                filter: filterModel.effectiveFormula.value,
                sort: grid.viewData.value?.sort ?? {},
                tableKey,
                userViewKey: init.tabId as string,
            };
        });
        effect(() => {
            const input = requestedRun.value;
            untracked(() => dispatchRuntime({ type: 'configureTableRun', input }));
        });

        const activeRun = computed<TableV2TableRun | null>(() =>
            runtimeState.value.type === 'uninitialized' ? null : runtimeState.value.run,
        );
        const tableQueryModel = new TableV2TableQueryModel({
            run: activeRun,
            table: init.table,
            userViewKey: init.tabId as string,
        });
        effect(() => () => tableQueryModel[Symbol.dispose]());

        effect(() => {
            const state = runtimeState.value;
            if (state.type === 'uninitialized') return;
            const observation = {
                connectionStatus: init.table.connectionStatus.value,
                error: init.table.errorSignal.value,
                failureCount: init.table.failureCount.value,
            };
            untracked(() =>
                dispatchRuntime({ type: 'observeTableRun', generation: state.run.generation, observation }),
            );
        });

        const eventBus: TableV2WidgetEventBus = MergedEventBus.from<WidgetContainerChannelSliceEventBus>(
            channelSlice.eventBus,
        )
            .merge<EventBus<TableWidgetSignals, TableWidgetEvents>>(tableSelection.eventBus)
            .merge<StickyFilterModelEventBus>(stickyFilter.eventBus)
            .merge<WidgetContainerTableSliceEventBus>(tableSlice.eventBus)
            .merge<WidgetContainerTabSliceEventBus>(widgetTabSlice.eventBus);

        return {
            classOptions: tableSelection.classOptions,
            columnTemplate: grid.columnTemplate,
            dirty: computed(() => grid.dirty.value || tableSelection.dirty.value || stickyFilter.dirty.value),
            eventBus,
            filteredTableKeyOptions: tableSelection.filteredTableKeyOptions,
            resolvedColumns: grid.resolvedColumns,
            tableKeyResultSummary: tableSelection.tableKeyResultSummary,
            tableSettingsOpen: tableSelection.tableSettingsOpen,
            searchQuery: tableSelection.searchQuery,
            selectedRowIds: linkingModel.selectedRowIds,
            selectedTableKey: tableSelection.selectedTableKey,
            state: computed(() => runtimeState.value),
            symbolIdField: grid.symbolIdField,
            tableClass: tableSelection.tableClass,
            tableKeyOptions: tableSelection.tableKeyOptions,
            viewData: grid.viewData,
            selectTableKey(value: string): void {
                tableSelection.selectTableKey(value);
            },
            setTableSettingsOpen(open: boolean): void {
                tableSelection.setTableSettingsOpen(open);
            },
            setSearchQuery(query: string): void {
                tableSelection.setSearchQuery(query);
            },
            setTableClass(tableClass: TableClass | null): void {
                tableSelection.setTableClass(tableClass);
            },
            updateRowSelection(change: GridRowSelectionChange): void {
                linkingModel.updateRowSelection(change);
            },
            updateColumnTemplate(columnTemplate: ColumnTemplate): void {
                grid.updateColumnTemplate(columnTemplate);
            },
            toJSON(): TableV2WidgetState {
                return { ...grid.toJSON(), ...tableSelection.toJSON(), ...stickyFilter.toJSON() };
            },
            markClean(): void {
                grid.markClean();
                tableSelection.markClean();
                stickyFilter.markClean();
            },
        };
    },
);

export type TableV2SelfOwnedModel = InstanceType<typeof TableV2SelfOwnedModel>;
